import {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { styled } from 'styled-components';
import { AsYouType } from 'libphonenumber-js';

import * as api from '../../api';
import ProviderImage from '../ProviderImage';
import { constructAddress, getProviderNameFull } from '../../utils/provider';
import StarRating from './starRating';
import EhrTimeSlots from '../Chat/Conversation/Message/Intent/Rescheduling/ehrTimeSlots';
import { mockEhrTimeSlots } from '../../utils/mock';
import { ShowMoreButton } from '../common';
import { ConfigurationContext } from '../../context/configurationContext';

interface Props extends PropsWithChildren {
  provider: api.User.UserWithProfileAndPreference;
  disabled?: boolean;
  onAppointmentSelected?: (ehrTimeSlot: api.EhrTimeSlot.EhrTimeSlot) => void;
  showNotionalSlots?: boolean;
  disableTopBorderRadius?: boolean;
  isOnlyOneCard?: boolean;
}

const ProviderCard: React.FC<Props> = ({
  provider,
  disabled,
  onAppointmentSelected,
  disableTopBorderRadius,
  isOnlyOneCard,
  showNotionalSlots,
  children,
}) => {
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [slots, setSlots] = useState<api.EhrTimeSlot.EhrTimeSlot[]>([]);
  const [showMorePractices, setShowMorePractices] = useState(false);
  const configurationContext = useContext(ConfigurationContext);

  const createClickedLog = useCallback(
    async (
      action:
        | api.Log.AssistantAuditAction.AssistantPatientClickedProviderPhoneNumber
        | api.Log.AssistantAuditAction.AssistantPatientClickedProviderProfile,
      providerUserId: string,
    ) => {
      if (!configurationContext?.id) return;
      if (!configurationContext.sessionId) return;
      try {
        await api.client.Assistant.createLog({
          action,
          assistantId: configurationContext.id,
          sessionId: configurationContext.sessionId,
          providerUserId,
        });
      } catch (error) {
        console.error('Could not add log', error);
      }
    },
    [configurationContext?.id, configurationContext?.sessionId],
  );

  useEffect(() => {
    if (!showNotionalSlots) return;
    setIsLoadingSlots(true);
    setTimeout(() => {
      setSlots(mockEhrTimeSlots());
      setIsLoadingSlots(false);
    }, 1400);
  }, [showNotionalSlots]);

  const numberOfHiddenPractices = provider.practices.length - 1;
  const providerInfoExtra = (
    <>
      {provider.practices.length ? (
        <TextSection $marginTop={8}>
          {(!showMorePractices
            ? [provider.practices[0]]
            : provider.practices
          ).map((practice) => (
            <TextSection key={practice.name} $marginTop={8}>
              {practice.name} - {constructAddress(practice)}
              {provider.practices.length > 1 && !showMorePractices && (
                <ShowMoreButton
                  type="button"
                  disabled={disabled}
                  onClick={() => setShowMorePractices(true)}
                >
                  +{numberOfHiddenPractices} location
                  {numberOfHiddenPractices === 1 ? '' : 's'}
                </ShowMoreButton>
              )}
            </TextSection>
          ))}
        </TextSection>
      ) : null}
      {provider.profile?.spokenLanguages?.length ? (
        <TextSection $marginTop={8}>
          Languages:{' '}
          {provider.profile?.spokenLanguages
            .map(
              (lang) => api.Language.languageMap[lang as api.Language.Language],
            )
            .join(', ')}
        </TextSection>
      ) : null}
      {provider.preference?.acceptsNewPatients ? (
        <div>
          <Badge>Accepts new patients</Badge>
        </div>
      ) : null}
    </>
  );

  const specialties = provider.profile?.specialtiesRaw
    ? provider.profile?.specialtiesRaw.join(', ')
    : provider.profile?.specialties
      ? provider.profile.specialties
          ?.map((specialty) => api.Common.SpecialtyMap[specialty])
          .join(', ')
      : undefined;

  return (
    <CardWrapper
      $disableTopBorderRadius={disableTopBorderRadius}
      $isOnlyOneCard={isOnlyOneCard}
      data-test-id="provider-card"
    >
      <CardContent>
        <ProviderInfoContainer>
          <ProviderImage
            provider={provider}
            url={provider.preference?.providerDirectoryUrl}
            onLinkClicked={() =>
              createClickedLog(
                api.Log.AssistantAuditAction
                  .AssistantPatientClickedProviderProfile,
                provider.id,
              )
            }
          />
          <ProviderInfo>
            {provider.preference?.providerDirectoryUrl ? (
              <ProviderNameLink
                href={provider.preference?.providerDirectoryUrl}
                target="_blank"
                onClick={() =>
                  createClickedLog(
                    api.Log.AssistantAuditAction
                      .AssistantPatientClickedProviderProfile,
                    provider.id,
                  )
                }
              >
                {getProviderNameFull({ provider })}
              </ProviderNameLink>
            ) : (
              <ProviderName>{getProviderNameFull({ provider })}</ProviderName>
            )}
            {specialties ? (
              <TextSection $marginTop={6}>{specialties}</TextSection>
            ) : null}
            {provider.rating?.reputation ? (
              <StarRating ratingWithCount={provider.rating.reputation} />
            ) : null}
            <DesktopExtraInfo>{providerInfoExtra}</DesktopExtraInfo>
          </ProviderInfo>
        </ProviderInfoContainer>
        <MobileExtraInfo>{providerInfoExtra}</MobileExtraInfo>
        <ProviderAction>
          {(slots.length || isLoadingSlots) && onAppointmentSelected ? (
            <EhrTimeSlotWrapper>
              <EhrTimeSlots
                slots={slots ?? []}
                timezone="America/Los_Angeles"
                disabled={disabled ?? false}
                onAppointmentSelected={onAppointmentSelected}
                isLoading={isLoadingSlots ?? false}
              />
            </EhrTimeSlotWrapper>
          ) : provider.preference?.schedulingLink ? (
            <LinkButton
              target="_blank"
              href={
                provider.preference.schedulingLink.includes('?')
                  ? `${provider.preference.schedulingLink}&source=ASSISTANT`
                  : `${provider.preference.schedulingLink}?source=ASSISTANT`
              }
            >
              {provider.preference.ableToSchedule === false
                ? 'View Profile'
                : 'Schedule Appointment'}
            </LinkButton>
          ) : provider.phoneNumber ? (
            <LinkButton
              target="_blank"
              href={`tel:${provider.phoneNumber}`}
              onClick={() =>
                createClickedLog(
                  api.Log.AssistantAuditAction
                    .AssistantPatientClickedProviderPhoneNumber,
                  provider.id,
                )
              }
            >
              {new AsYouType('US').input(provider.phoneNumber)}
            </LinkButton>
          ) : null}
        </ProviderAction>
      </CardContent>
      {children}
    </CardWrapper>
  );
};

const CardWrapper = styled.div.attrs<{
  $disableTopBorderRadius?: boolean;
  $isOnlyOneCard?: boolean;
}>((props) => ({
  $disableTopBorderRadius: props.$disableTopBorderRadius ?? false,
  $isOnlyOneCard: props.$isOnlyOneCard ?? false,
}))`
  border: 1px solid var(--border-color);
  border-radius: ${(props) =>
    props.$disableTopBorderRadius ? '0 0 4px 4px' : '4px'};
  font-size: var(--font-size);
  padding: 20px;
  min-height: 145px;

  @media (max-width: 680px) {
    flex-wrap: wrap;
    width: ${(props) =>
      props.$isOnlyOneCard ? '100%' : 'calc(80vw - 1.5rem)'};
    min-width: 295px;
  }
`;

const CardContent = styled.div`
  display: flex;
  justify-content: space-between;

  @media (max-width: 680px) {
    flex-direction: column;
  }
`;

const ProviderInfoContainer = styled.div`
  display: flex;
`;

const ProviderInfo = styled.div`
  margin-left: 15px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const ProviderAction = styled.div`
  @media (max-width: 680px) {
    width: 100%;
  }
`;

const LinkButton = styled.a`
  text-decoration: none;
  color: var(--color-white);
  background-color: var(--action-color);
  padding: 5px 10px;
  border-radius: var(--button-border-radius);
  white-space: nowrap;
  font-size: 16px;

  @media (max-width: 680px) {
    display: block;
    text-align: center;
  }
`;

const ProviderName = styled.strong`
  display: block;
  margin-bottom: -4px;
  font-size: 16px;
`;

const ProviderNameLink = styled.a`
  font-weight: bold;
  display: block;
  margin-bottom: -4px;
  font-size: 16px;
  cursor: pointer;
  color: var(--font-color);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Badge = styled.span`
  font-size: 12px;
  color: var(--color-shade-70);
  background-color: var(--color-shade-30);
  padding: 1px 2px;
`;

const TextSection = styled.div.attrs<{ $marginTop?: number }>((props) => ({
  $marginTop: props.$marginTop ?? 0,
}))`
  font-size: 14px;
  margin-top: ${(props) => `${props.$marginTop}px`};
`;

const DesktopExtraInfo = styled.div`
  display: block;

  @media (max-width: 680px) {
    display: none;
  }
`;

const MobileExtraInfo = styled.div`
  display: none;
  margin-top: 10px;

  @media (max-width: 680px) {
    display: block;
  }
`;

const EhrTimeSlotWrapper = styled.div`
  width: 275px;
  margin-left: 15px;

  @media (max-width: 680px) {
    margin-left: 0;
  }
`;

export default ProviderCard;
